// Types for Stats Graph visualization

// Success rate color thresholds
export const STATS_COLORS = {
  HIGH: "#00c889", // >= 80% success rate
  MEDIUM: "#eab308", // 50-80% success rate
  LOW: "#ef4444", // < 50% success rate
  NO_DATA: "#9ca3af", // no data
  NOT_VISITED: "#d1d5db", // not visited (session view)
  NON_SCORED: "#6366f1", // non-scored interaction (indigo) - distinct from scored
} as const;

// Success rate thresholds (percentages)
export const STATS_THRESHOLDS = {
  HIGH: 80,
  MEDIUM: 50,
} as const;

/**
 * Get color based on success rate
 */
export const getSuccessRateColor = (successRate: number | null | undefined): string => {
  if (successRate == null) return STATS_COLORS.NO_DATA;
  if (successRate >= STATS_THRESHOLDS.HIGH) return STATS_COLORS.HIGH;
  if (successRate >= STATS_THRESHOLDS.MEDIUM) return STATS_COLORS.MEDIUM;
  return STATS_COLORS.LOW;
};

/**
 * Stats data attached to a node
 */
export interface NodeStats {
  successRate: number; // 0-100 percentage
  attemptCount: number;
  avgDuration: number; // in seconds
  totalScore?: number;
  totalScoreMax?: number;
}

/**
 * A node in the stats graph (scene or asset/interaction)
 */
export interface StatsNode {
  // From graph structure
  contentId: number;
  contentTypeId: number;
  name: string;
  type: "scene" | "asset";
  modelName: string;
  x: number;
  y: number;

  // Scene-specific
  isStartScene?: boolean;
  sceneType?: string; // video, image, 360, etc.

  // Asset-specific
  assetType?: "image" | "video" | "location" | "sound" | "control" | "model3d" | "effect";

  // Stats data (may be undefined if no data)
  stats?: NodeStats;

  // Session view: whether this node was visited
  visited?: boolean;

  // Whether this interaction is scored (has score_max > 0)
  // Used to visually distinguish scored vs non-scored interactions
  isScored?: boolean;
}

/**
 * A link/edge between nodes
 */
export interface StatsNodeLink {
  from: StatsNode;
  to: StatsNode;
  linksToScene?: boolean;
  // Optional flow frequency (for aggregate view)
  flowCount?: number;
}

/**
 * Layout rectangle for the graph
 */
export interface GraphLayout {
  width: number;
  height: number;
  x: number;
  y: number;
}

/**
 * Complete graph data for stats visualization
 */
export interface GraphStatsData {
  nodes: StatsNode[];
  links: StatsNodeLink[];
  layout: GraphLayout;
  isLoading: boolean;
  error?: string;
}

/**
 * Interaction stats from the API (used for merging with graph)
 */
export interface InteractionStatData {
  contentId: number;
  contentName: string;
  contentType: string;
  sceneId: number | null;
  sceneName: string | null;
  attemptCount: number;
  successRate: number;
  avgScore: number;
  avgDuration: number;
}

/**
 * Scene stats from the API (used for merging with graph)
 */
export interface SceneStatData {
  sceneId: number;
  sceneName: string;
  successRate: number;
  totalScore: number;
  totalScoreMax: number;
  attemptCount: number;
}
